import React, { useCallback, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import Backdrop from "@mui/material/Backdrop";
import {
  modelsManagementService,
  myDesignService,
  myExpariencesService,
} from "../../services";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Switch,
} from "@mui/material";
import { Typography, TextField } from "@mui/material";
import Slide from "@mui/material/Slide";
import { useSnackbar } from "notistack";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import EventIcon from "@mui/icons-material/Event";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import CreateIcon from "@mui/icons-material/Create";
import { normalizeText } from "../../helpers";
import { useTranslation } from "react-i18next";
import AdsApp from "./AdsApp";
import Badge from "@mui/material/Badge";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Logo from "../../assets/logoStudioPBADS.png";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ConfirmationDialog from "../dialog/ConfirmationDialog";
import { Add, Check, NotificationsActive } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  deleteExparience,
  fetchMyExpariences,
  updatePublication,
} from "../../reducers/expariences/expariencesThunks";
import {
  fetchZones,
  sendNotification,
  updateRedirect,
} from "../../reducers/myDesigns/myDesignsThunks";
import {
  closeBackdrop,
  openBackdrop,
  resetDeletedDesign,
} from "../../reducers/myDesigns/myDesignsSlice";
import { useTheme } from "@emotion/react";
import Hourglass from "../../assets/hourglass.json";
import Lottie from "lottie-react";
import ResponsiveDialogManager from "../dialog/ResponsiveDialogManager";
import GltfLoader from "../configurator/configuratorForms/GltfLoader";
import InfiniteScroll from "react-infinite-scroll-component";
import ImageAndTitle from "../dragNDrop/draggableElements/ImageAndTitle";

const PREFIX = "BackdropManager";

const classes = {
  root: `${PREFIX}-root`,
  switchBase: `${PREFIX}-switchBase`,
  thumb: `${PREFIX}-thumb`,
  track: `${PREFIX}-track`,
  checked: `${PREFIX}-checked`,
  backdrop: `${PREFIX}-backdrop`,
  zoneBottomMenu: `${PREFIX}-zoneBottomMenu`,
  imageZoneMenu: `${PREFIX}-imageZoneMenu`,
  zoneName: `${PREFIX}-zoneName`,
  listItemIcon: `${PREFIX}-listItemIcon`,
  redirectContainer: `${PREFIX}-redirectContainer`,
  redirectTitle: `${PREFIX}-redirectTitle`,
  redirectTextField: `${PREFIX}-redirectTextField`,
  uploadContainer: `${PREFIX}-uploadContainer`,
  uploadTitle: `${PREFIX}-uploadTitle`,
  uploadField: `${PREFIX}-uploadField`,
  uploadButton: `${PREFIX}-uploadButton`,
  fileName: `${PREFIX}-fileName`,
  uploadingContainer: `${PREFIX}-uploadingContainer`,
  loadingIcon: `${PREFIX}-loadingIcon`,
  imageContainer: `${PREFIX}-imageContainer`,
  menuContainer: `${PREFIX}-menuContainer`,
  modifySelect: `${PREFIX}-modifySelect`,
  selectStyle: `${PREFIX}-selectStyle`,
  closeBackdropIcon: `${PREFIX}-closeBackdropIcon`,
  selectMenuStyle: `${PREFIX}-selectMenuStyle`,
  captionError: `${PREFIX}-captionError`,
  buttonContainer: `${PREFIX}-buttonContainer`,
  hourglassIcon: `${PREFIX}-hourglassIcon`,
  hourglassContainer: `${PREFIX}-hourglassContainer`,
  paperContainer: `${PREFIX}-paperContainer`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`&.${classes.backdrop}`]: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },

  [`& .${classes.zoneBottomMenu}`]: {
    width: "100%",
    minHeight: "150px",
    position: "absolute",
    bottom: "0",
    backgroundColor: "#303030",
    borderTopLeftRadius: "4px",
    borderTopRightRadius: "4px",
    padding: "16px",
    display: "flex",
    flexDirection: "row",
  },

  [`& .${classes.imageZoneMenu}`]: {
    width: "120px",
    filter: "blur(1px)",
    position: "relative",
    top: "50%",
    transform: "translateY(-50%)",
    marginTop: "1.5rem",
  },

  [`& .${classes.zoneName}`]: {
    position: "absolute",
    top: "1.5rem",
    transform: "translateY(-50%)",
    textAlign: "center",
    color: theme.palette.primary.dark,
    fontWeight: 400,
  },

  [`& .${classes.listItemIcon}`]: {
    width: "38px",
    height: "28px",
    color: theme.palette.primary.main,
  },

  [`& .${classes.redirectContainer}`]: {
    width: "100%",
    maxWidth: "900px",
    backgroundColor: "#303030",
    borderRadius: "4px",
    padding: "16px",
    textAlign: "center",
    margin: "0 auto",
  },

  [`& .${classes.redirectTitle}`]: {
    color: theme.palette.primary.main,
  },

  [`& .${classes.redirectTextField}`]: {
    maxWidth: "600px",
  },

  [`& .${classes.uploadContainer}`]: {
    position: "relative",
    width: "100%",
    maxWidth: "900px",
    backgroundColor: "#303030",
    borderRadius: "4px",
    padding: "16px",
    textAlign: "center",
    margin: "0 auto",
  },

  [`& .${classes.uploadTitle}`]: {
    color: theme.palette.primary.main,
  },

  [`& .${classes.uploadField}`]: {
    maxWidth: "600px",
    margin: "0 auto",
    textAlign: "left",
    marginBottom: "2rem",
    marginTop: "1rem",
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.uploadButton}`]: {
    marginRight: "0.5rem",
  },

  [`& .${classes.fileName}`]: {
    transform: "translateY(7px)",
  },

  [`& .${classes.uploadingContainer}`]: {
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.loadingIcon}`]: {
    margin: "1rem auto",
  },

  [`& .${classes.imageContainer}`]: {
    margin: "0 auto",
    width: "120px",
  },

  [`& .${classes.menuContainer}`]: {
    margin: "0 auto",
    marginTop: "1.5rem",
  },

  [`& .${classes.modifySelect}`]: {
    margin: "2rem 0 1rem 0",
    width: "100%",
    maxWidth: "350px",
  },

  [`& .${classes.selectStyle}`]: {
    width: "100%",
  },

  [`& .${classes.closeBackdropIcon}`]: {
    position: "absolute",
    top: "1rem",
    right: "1rem",
  },

  [`& .${classes.selectMenuStyle}`]: {
    backgroundColor: "#424242",
  },

  [`& .${classes.captionError}`]: {
    color: "#F44336 !important",
  },

  [`& .${classes.buttonContainer}`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  [`& .${classes.hourglassContainer}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "2rem 0",
  },

  [`& .${classes.hourglassIcon}`]: {
    width: "60px",
    height: "60px",
  },

  [`& .${classes.paperContainer}`]: {
    [theme.breakpoints.between("xs", "md")]: {
      maxWidth: "calc(100vw - 17vw)",
      height: "110px",
    },
    maxWidth: "650px",
    flexDirection: "row",
    flexBasis: "inherit",
    flexWrap: "inherit",
    padding: "0.5rem 0.5rem 1rem 0.5rem",
    height: "160px",
    overflowY: "hidden",
    overflowX: "auto",
    display: "flex",
    backgroundColor: "#2b2b2b",
    "&::-webkit-scrollbar": {
      width: "7px",
      height: "7px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
  },
}));

export default function BackdropManager() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useNavigate();
  const loading = useSelector(
    (state) => state.myDesigns.currentDesign?.loading
  );
  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role
  );
  const backdropStatus = useSelector(
    (state) => state.myDesigns.backdropRequest.status
  );
  const backdropFrom = useSelector(
    (state) => state.myDesigns.backdropRequest.from
  );
  const isDesignNull = useSelector(
    (state) => state.myDesigns.currentDesign?.designNull
  );

  const LoadingComponent = () => {
    return <CircularProgress size={50} color="inherit" />;
  };

  const mySendNotification = async (infos) => {
    try {
      const thunk = await dispatch(
        sendNotification({
          frameId: infos.frameId,
          frameName: infos.title,
          label: infos.target,
          zoneName: infos.label,
        })
      );
      if (thunk.meta.requestStatus === "rejected")
        throw new Error("recovery error");
      enqueueSnackbar(t("toasts.notificationSent"), { variant: "success" });
    } catch (err) {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  const ZoneActionMenu = () => {
    const infos = useSelector((state) => state.myDesigns.backdropRequest.infos);
    // modify frameId to id (which is the zoneId) once we will have images for zones
    const frameUrl =
      process.env.REACT_APP_API_URL +
      "/public/frames/" +
      infos.frameId +
      "/image";
    const zoneUrl = `${process.env.REACT_APP_API_URL}/frames/${infos.frameId}/zones/${infos.target}/${infos.index}/image`;
    const [url, setUrl] = React.useState(zoneUrl);
    //const url = process.env.REACT_APP_API_URL + '/public/frames/' + infos.frameId + '/image';

    return (
      <Slide direction="up" in={backdropStatus} mountOnEnter unmountOnExit>
        <StyledDiv>
          <div
            className={classes.zoneBottomMenu}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="body1" className={classes.zoneName}>
              {infos.label}
            </Typography>
            <div className={classes.imageContainer}>
              <img
                src={url}
                alt={infos.label}
                className={classes.imageZoneMenu}
                onError={(e) => setUrl(frameUrl)}
              />
            </div>
            <div className={classes.menuContainer}>
              <List>
                {(infos.saveAccess === 1 ||
                  infos.uploadAccess === 1 ||
                  isAdmin === "ADMIN") && (
                  <ListItem
                    role={undefined}
                    dense
                    button
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(openBackdrop("toto"));
                    }}
                  >
                    <ListItemIcon>
                      <CreateIcon className={classes.listItemIcon} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("myDesign.zones.actionsMenu.modify")}
                    />
                  </ListItem>
                )}
                {(infos.scheduleAccess === 1 || isAdmin === "ADMIN") && (
                  <ListItem
                    role={undefined}
                    dense
                    button
                    onClick={() => {
                      dispatch(closeBackdrop());
                      history(`/mydesign/${infos.frameId}/zone/${infos.id}`, {
                        state: {
                          myDesign: infos,
                          index: 1,
                        },
                      });
                    }}
                  >
                    <ListItemIcon>
                      <Badge
                        badgeContent={infos.planifications}
                        color="secondary"
                        overlap="rectangular"
                      >
                        <EventIcon className={classes.listItemIcon} />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText
                      primary={t("myDesign.zones.actionsMenu.schedule")}
                    />
                  </ListItem>
                )}
                {(infos.previewAccess === 1 || isAdmin === "ADMIN") && (
                  <ListItem
                    role={undefined}
                    dense
                    button
                    onClick={() => window.open(infos.targetUrl, "_blank")}
                  >
                    <ListItemIcon>
                      <FindInPageIcon className={classes.listItemIcon} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("myDesign.zones.actionsMenu.preview")}
                    />
                  </ListItem>
                )}
                {(infos.notifyAccess === 1 || isAdmin === "ADMIN") && (
                  <ListItem
                    role={undefined}
                    dense
                    button
                    onClick={() => mySendNotification(infos)}
                  >
                    <ListItemIcon>
                      <SettingsInputAntennaIcon
                        className={classes.listItemIcon}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("myDesign.zones.actionsMenu.notify")}
                    />
                  </ListItem>
                )}
                <ListItem
                  role={undefined}
                  dense
                  button
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(closeBackdrop());
                    history(`/mydesign/${infos.frameId}/zone/${infos.id}`, {
                      state: {
                        myDesign: infos,
                      },
                    });
                  }}
                >
                  <ListItemIcon>
                    <i
                      class="fas fa-cog"
                      style={{
                        color: "#D3AF5F",
                        margin: "0 0.25rem",
                        fontSize: "26px",
                      }}
                    ></i>
                  </ListItemIcon>
                  <ListItemText
                    primary={t("myDesign.zones.actionsMenu.options")}
                  />
                </ListItem>
              </List>
            </div>
          </div>
        </StyledDiv>
      </Slide>
    );
  };

  const ExparienceZoneActionMenu = () => {
    const infos = useSelector((state) => state.myDesigns.backdropRequest.infos);
    const [url, setUrl] = React.useState(infos.url);
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const GoldenSwitchVariant = Switch;

    const deleteMyExparience = async (id) => {
      try {
        const thunk = await dispatch(deleteExparience(id));
        if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
        enqueueSnackbar(t("toasts.exparienceDeletedSuccess"), {
          variant: "success",
        });
        try {
          const thunk = await dispatch(
            fetchMyExpariences({ offset: 0, limit: 20, reset: true })
          );
          if (thunk.meta.requestStatus === "rejected")
            throw new Error("offline");
        } catch {
          enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        }
      } catch {
        enqueueSnackbar(t("toasts.exparienceDeletedError"), {
          variant: "error",
        });
      }
    };

    const updateMyPublication = async (id, published, isAdmin) => {
      try {
        const thunk = await dispatch(
          updatePublication({ id, value: published, isAdmin })
        );
        if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
        enqueueSnackbar(t("toasts.updatePublicationExparienceSuccess"), {
          variant: "success",
        });
      } catch {
        enqueueSnackbar(t("toasts.updatePublicationExparienceError"), {
          variant: "error",
        });
      }
    };

    return (
      <React.Fragment>
        <ConfirmationDialog
          open={openConfirmation}
          setOpen={setOpenConfirmation}
          element={infos.exparience}
          onAgree={() => {
            deleteMyExparience(infos.exparience._id);
            dispatch(closeBackdrop());
          }}
          mode="deleteExparience"
        />
        <Slide direction="up" in={backdropStatus} mountOnEnter unmountOnExit>
          <StyledDiv>
            <div
              className={classes.zoneBottomMenu}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography variant="body1" className={classes.zoneName}>
                {infos.exparience.displayName}
              </Typography>
              <div className={classes.imageContainer}>
                <img
                  src={url}
                  alt={infos.exparience.displayName}
                  className={classes.imageZoneMenu}
                  onError={(e) => setUrl(Logo)}
                />
              </div>
              <div className={classes.menuContainer}>
                <List>
                  <ListItem
                    dense
                    button
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(closeBackdrop());
                      updateMyPublication(
                        infos.exparience.id,
                        !infos.exparience.published,
                        isAdmin
                      );
                    }}
                  >
                    <ListItemIcon>
                      <GoldenSwitchVariant
                        disabled
                        checked={
                          infos.exparience.published ||
                          infos.exparience.published === null
                            ? true
                            : false
                        }
                        name="publish/unpublish"
                        classes={{
                          root: classes.root,
                          switchBase: classes.switchBase,
                          thumb: classes.thumb,
                          track: classes.track,
                          checked: classes.checked,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        infos.exparience.published ||
                        infos.exparience.published === null
                          ? t("myExpariences.unpublishExparience")
                          : t("myExpariences.publishExparience")
                      }
                    />
                  </ListItem>
                  <ListItem
                    role={undefined}
                    dense
                    button
                    onClick={() => {
                      myExpariencesService
                        .sendNotification(infos.exparience._id, isAdmin)
                        .then(() => {
                          enqueueSnackbar(t("toasts.notificationSent"), {
                            variant: "success",
                          });
                        })
                        .catch((err) => {
                          if (err === 403)
                            enqueueSnackbar(t("toasts.unauthorizedError"), {
                              variant: "error",
                            });
                          else
                            enqueueSnackbar(t("toasts.unknownError"), {
                              variant: "error",
                            });
                        });
                      dispatch(closeBackdrop());
                    }}
                  >
                    <ListItemIcon>
                      <NotificationsActive className={classes.listItemIcon} />
                    </ListItemIcon>
                    <ListItemText primary={t("myExpariences.notify")} />
                  </ListItem>
                  <ListItem
                    role={undefined}
                    dense
                    button
                    onClick={(e) => {
                      setOpenConfirmation(true);
                    }}
                  >
                    <ListItemIcon>
                      <DeleteForeverIcon className={classes.listItemIcon} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("myExpariences.deleteExparience")}
                    />
                  </ListItem>
                </List>
              </div>
            </div>
          </StyledDiv>
        </Slide>
      </React.Fragment>
    );
  };

  const ModifyLink = () => {
    const infos = useSelector((state) => state.myDesigns.backdropRequest.infos);
    const [targetUrl, setTargetUrl] = useState("");
    const [title, setTitle] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [message, setMessage] = React.useState("");

    const updateMyRedirect = async (
      infos,
      url,
      title,
      description,
      customMessage
    ) => {
      try {
        const thunk = await dispatch(
          updateRedirect({
            infos,
            newUrl: url,
            title,
            description,
            message: customMessage,
          })
        );
        if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
        enqueueSnackbar(t("toasts.redirectLinkModified"), {
          variant: "success",
        });
        dispatch(closeBackdrop());
      } catch {
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      }
    };

    return (
      <StyledDiv>
        <div>
          <div className={classes.inputsContainer}>
            <TextField
              variant="outlined"
              className={classes.redirectTextField}
              margin="normal"
              required
              fullWidth
              id="title"
              label="Title"
              name="title"
              autoComplete="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              variant="outlined"
              className={classes.redirectTextField}
              margin="normal"
              required
              fullWidth
              id="description"
              label="Description"
              name="description"
              autoComplete="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <TextField
            variant="outlined"
            className={classes.redirectTextField}
            margin="normal"
            required
            fullWidth
            id="targetUrl"
            label={t("myDesign.zones.redirect.targetUrl")}
            name="targetUrl"
            autoComplete="targetUrl"
            autoFocus
            value={targetUrl}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setTargetUrl(e.target.value)}
          />
          <TextField
            variant="outlined"
            className={classes.redirectTextField}
            margin="normal"
            fullWidth
            id="customMessage"
            label={t("myDesign.zones.redirect.customMessage")}
            name="customMessage"
            autoComplete="customMessage"
            autoFocus
            value={message}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
        <div className={classes.buttonContainer}>
          {!(targetUrl !== infos.targetUrl) && (
            <Typography variant="caption" className={classes.captionError}>
              {t("myDesign.zones.modify.sameUrlError")}
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            disabled={
              !(targetUrl !== infos.targetUrl) ||
              targetUrl.length <= 0 ||
              title === "" ||
              description === ""
            }
            onClick={() => {
              let tmpTitle = normalizeText(title);
              let tmpDescription = normalizeText(description);
              let tmpCustomMessage = normalizeText(message);
              updateMyRedirect(
                infos,
                targetUrl,
                tmpTitle,
                tmpDescription,
                tmpCustomMessage
              );
            }}
          >
            {t("myDesign.zones.redirect.saveButton")}
          </Button>
        </div>
      </StyledDiv>
    );
  };

  const UploadVideo = (props) => {
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [message, setMessage] = useState("");
    const [percents, setPercents] = useState(0);

    React.useEffect(() => {
      setFile(null);
    }, [props.mod]);

    const fetchMyZones = async (id) => {
      try {
        const thunk = await dispatch(fetchZones(id));
        if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      } catch {
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      }
    };

    const handleProgress = (percent) => {
      setPercents(percent);
    };

    if (props.isUploading) {
      return (
        <StyledDiv>
          <div className={classes.uploadingContainer}>
            {percents === 100 ? (
              <div className={classes.hourglassContainer}>
                <Lottie
                  animationData={Hourglass}
                  loop={true}
                  className={classes.hourglassIcon}
                />
                <Typography variant="body1">
                  {t("myDesign.zones.uploadVideo.videoProcessing")}
                </Typography>
              </div>
            ) : (
              <>
                <CircularProgressWithLabel
                  size={50}
                  color="inherit"
                  className={classes.loadingIcon}
                  value={percents}
                />
                <Typography variant="body1">
                  {t("loaders.uploading")}
                </Typography>
              </>
            )}
          </div>
        </StyledDiv>
      );
    } else {
      return (
        <div key="form-video">
          <StyledDiv>
            <div>
              <TextField
                variant="outlined"
                className={classes.redirectTextField}
                margin="normal"
                required
                fullWidth
                id="title"
                label={t("myDesign.zones.uploadVideo.titleVideo")}
                name="title"
                autoComplete="title"
                autoFocus
                value={title}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setTitle(e.target.value)}
              />
              <TextField
                variant="outlined"
                className={classes.redirectTextField}
                margin="normal"
                required
                fullWidth
                id="description"
                label={t("myDesign.zones.uploadVideo.descriptionVideo")}
                name="description"
                autoComplete="description"
                // autoFocus
                value={description}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setDescription(e.target.value)}
              />
              {props.mod !== "multi" && (
                <TextField
                  variant="outlined"
                  className={classes.redirectTextField}
                  margin="normal"
                  fullWidth
                  id="customMessage"
                  label={t("myDesign.zones.uploadVideo.customMessage")}
                  name="customMessage"
                  autoComplete="customMessage"
                  // autoFocus
                  value={message}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setMessage(e.target.value)}
                />
              )}
            </div>
            <div className={classes.uploadField}>
              <Button
                variant="contained"
                color="primary"
                component="label"
                className={classes.uploadButton}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  if (e.target.files !== undefined) setFile(e.target.files);
                  else return null;
                }}
              >
                {t("myDesign.zones.uploadVideo.selectFileButton")}
                <input
                  type="file"
                  multiple={props.mod === "multi"}
                  id="button-add-file"
                  accept={
                    props.mod === "image"
                      ? "image/*"
                      : props.mod === "upload"
                      ? "video/avi, video/wmv, video/mov, video/rm, video/ram, video/swf, video/flv, video/ogg, video/webm, video/mp4"
                      : "image/*, video/avi, video/wmv, video/mov, video/rm, video/ram, video/swf, video/flv, video/ogg, video/webm, video/mp4"
                  }
                  hidden
                />
              </Button>
              <div>
                {file !== null &&
                  [...file].map((element, index) => (
                    <Typography
                      variant="body1"
                      className={classes.fileName}
                      key={index + 1}
                    >
                      {element.name}
                    </Typography>
                  ))}
              </div>
            </div>
            <Button
              variant="contained"
              color="primary"
              disabled={
                title.length === 0 ||
                description.length === 0 ||
                file === null ||
                props.isUploading
              }
              onClick={(e) => {
                e.stopPropagation();
                props.setIsUploading(true);
                let tmpTitle = normalizeText(title);
                let tmpDescription = normalizeText(description);
                let tmpCustomMessage = normalizeText(message);
                if (props.mod !== "multi") {
                  myDesignService
                    .uploadVideo(
                      props.infos,
                      {
                        title: tmpTitle,
                        description: tmpDescription,
                        message: tmpCustomMessage,
                        file: file[0],
                      },
                      handleProgress
                    )
                    .then(() => {
                      props.setIsUploading(false);
                      enqueueSnackbar(t("toasts.uploadVideoSuccess"), {
                        variant: "success",
                      });
                      dispatch(closeBackdrop());
                      fetchMyZones(props.infos.frameId);
                    })
                    .catch((err) => {
                      if (err === 403)
                        enqueueSnackbar(t("toasts.unauthorizedError"), {
                          variant: "error",
                        });
                      else
                        enqueueSnackbar(t("toasts.unknownError"), {
                          variant: "error",
                        });
                      props.setIsUploading(false);
                    });
                } else {
                  myDesignService
                    .uploadListFiles(
                      props.infos,
                      {
                        title: tmpTitle,
                        description: tmpDescription,
                        file: file,
                      },
                      handleProgress
                    )
                    .then(() => {
                      props.setIsUploading(false);
                      enqueueSnackbar(t("toasts.uploadListFileSuccess"), {
                        variant: "success",
                      });
                      dispatch(closeBackdrop());
                      fetchMyZones(props.infos.frameId);
                    })
                    .catch((err) => {
                      if (err === 403)
                        enqueueSnackbar(t("toasts.unauthorizedError"), {
                          variant: "error",
                        });
                      else
                        enqueueSnackbar(t("toasts.uploadListFileError"), {
                          variant: "error",
                        });
                      props.setIsUploading(false);
                    });
                }
              }}
            >
              {t("myDesign.zones.uploadVideo.uploadButton")}
            </Button>
          </StyledDiv>
        </div>
      );
    }
  };

  const ModifyContent = () => {
    const infos = useSelector((state) => state.myDesigns.backdropRequest.infos);
    const [mod, setMod] = useState("upload");
    const [isUploading, setIsUploading] = useState(false);

    function handleModChange(e) {
      e.stopPropagation();
      e.preventDefault();
      setMod(e.target.value);
    }

    React.useEffect(() => {
      setMod(mod);
    }, [mod]);

    return (
      <Container onClick={(e) => e.stopPropagation()}>
        <StyledDiv>
          <div className={classes.uploadContainer}>
            {/* {!isUploading && (
              <IconButton
                className={classes.closeBackdropIcon}
                color="primary"
                aria-label="upload picture"
                component="span"
                onClick={() => {
                  dispatch(closeBackdrop());
                }}
                size="large"
              >
                <ClearIcon />
              </IconButton>
            )} */}
            <Typography variant="h3" className={classes.uploadTitle}>
              {t("myDesign.zones.modify.title")}
            </Typography>
            <FormControl className={classes.modifySelect}>
              <InputLabel id="demo-simple-select-outlined-label">
                {t("myDesign.zones.modify.contentType")}
              </InputLabel>
              <Select
                labelId="demo-simple-select-outlined-label"
                id="demo-simple-select-outlined"
                className={classes.selectStyle}
                value={mod}
                onChange={handleModChange}
                label={t("myDesign.zones.modify.contentType")}
                MenuProps={{ classes: { paper: classes.selectMenuStyle } }}
              >
                {(infos.uploadAccess === 1 || isAdmin === "ADMIN") && (
                  <MenuItem key="upload" value="upload">
                    {t("myDesign.zones.modify.videoContent")}
                  </MenuItem>
                )}
                {(infos.uploadAccess === 1 || isAdmin === "ADMIN") && (
                  <MenuItem key="image" value="image">
                    {t("myDesign.zones.modify.imageContent")}
                  </MenuItem>
                )}
                {(infos.uploadAccess === 1 || isAdmin === "ADMIN") && (
                  <MenuItem key="link" value="link">
                    {t("myDesign.zones.modify.linkContent")}
                  </MenuItem>
                )}
                {(infos.uploadAccess === 1 || isAdmin === "ADMIN") && (
                  <MenuItem key="multi" value="multi">
                    {t("myDesign.zones.modify.fileList")}
                  </MenuItem>
                )}
                {/* {(infos.uploadAccess === 1 && isAdmin === "ADMIN") && ( */}
                {isAdmin === "ADMIN" && (
                  <MenuItem key="model3D" value="model3D">
                    {t("myDesign.zones.modify.model3D")}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <div>
              {mod === "upload" || mod === "image" || mod === "multi" ? (
                <UploadVideo
                  mod={mod}
                  isUploading={isUploading}
                  setIsUploading={setIsUploading}
                  infos={infos}
                />
              ) : mod === "model3D" ? (
                <UploadModel
                  isUploading={isUploading}
                  setIsUploading={setIsUploading}
                  infos={infos}
                />
              ) : (
                <ModifyLink />
              )}
            </div>
          </div>
        </StyledDiv>
      </Container>
    );
  };

  const UploadModel = ({ isUpload, setIsUploading, infos }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [models, setModels] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [saving, setSaving] = useState({ lock: true, trigger: false });
    const [selectedModel, setSelectedModel] = useState(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");

    const fetchModels = useCallback(() => {
      // function to fetch models, res is the return from the creation api of gltfloader
      modelsManagementService
        .getAllConfigModels(isAdmin)
        .then((res) => {
          setModels(res);
        })
        .catch((err) => {
          console.log("err =>", err);
          enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        });
    }, [enqueueSnackbar, t]);

    // fetch les modeles dispo sur useffect [] et les afficher dans le select
    useEffect(() => {
      fetchModels();
    }, [fetchModels]);

    const handleSaveModel = () => {
      setIsUploading(true);
      myDesignService
        .uploadModel(infos.id, { model: selectedModel._id, title, description })
        .then(() => {
          enqueueSnackbar(t("toasts.uploadModelSuccess"), {
            variant: "success",
          });
          dispatch(closeBackdrop());
          setIsUploading(false);
        })
        .catch((err) => {
          console.log("err =>", err);
          setIsUploading(false);
          enqueueSnackbar(t("toasts.uploadModelError"), { variant: "error" });
        });
    };

    return (
      <StyledDiv>
        <ResponsiveDialogManager
          title={t("modelsManager.modelManagementDialogTitle")}
          fullScreen
          open={openDialog ? true : false}
          onClose={() => {
            setOpenDialog(false);
            setSaving({ lock: true, trigger: false });
          }}
          renderer={
            <GltfLoader
              modelManagement
              modelSaveOptions={saving}
              setLockSave={setSaving}
              fetchModels={fetchModels}
              setOpenDialog={setOpenDialog}
              currentModel={null}
              setCurrentModel={() => console.log("done")}
              displayAll
            />
          }
          toolbarActions={
            <Box>
              <IconButton
                className={classes.buttonSpaces}
                sx={{
                  backgroundColor: "#424242",
                  color: theme.palette.primary.main,
                  marginRight: "15px",
                  "&:hover": {
                    backgroundColor: "rgba(66,66,66,0.7)",
                    color: theme.palette.primary.main,
                  },
                }}
                disabled={saving.lock || saving.trigger}
                onClick={() => setSaving({ ...saving, trigger: true })}
              >
                {saving.trigger ? (
                  <CircularProgress size={28} sx={{ color: "#343434" }} />
                ) : (
                  <Check />
                )}
              </IconButton>
            </Box>
          }
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          <Typography variant="body1">
            {t("myDesign.zones.redirect.modelChoice")}
          </Typography>
          {!isUpload && (
            <IconButton
              variant="contained"
              color="primary"
              onClick={() => setOpenDialog(true)}
              size="small"
              sx={{
                marginLeft: "1rem",
                maxWidth: "80%",
                fontSize: "12px",
                backgroundColor: "#D3AF5F",
                color: "#424242",
                "&:hover": {
                  backgroundColor: "#424242",
                  color: "#D3AF5F",
                },
              }}
            >
              <Add />
            </IconButton>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "1rem",
            maxWidth: "650px",
            margin: "0 auto 1rem auto",
          }}
        >
          {!models ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size={28} color="primary" />
              <Typography variant="body1" sx={{ marginLeft: "1rem" }}>
                {t("loaders.fetching")}
              </Typography>
            </Box>
          ) : (
            <InfiniteScroll
              dataLength={models.totalItems}
              next={() => fetchModels()}
              hasMore={models.totalPages > models.currentPage}
              scrollableTarget={`upload-model-infinite-scroll`}
            >
              <StyledDiv>
                <Paper
                  elevation={10}
                  className={classes.paperContainer}
                  id={`upload-model-infinite-scroll`}
                >
                  {models.data.map((item, index) => {
                    const isSelected = selectedModel?._id === item._id;
                    return (
                      <Paper
                        elevation={13}
                        key={`base-list-${item._id}-${index + 1}`}
                        sx={{
                          border: isSelected ? "2px solid #d3af5f" : "none",
                          marginRight: "1rem",
                        }}
                        onClick={() => {
                          setSelectedModel(item);
                          setTitle(item.title);
                          setDescription(item.description.initial);
                        }}
                      >
                        <ImageAndTitle
                          image={item.previewUrl}
                          title={item.name}
                        />
                      </Paper>
                    );
                  })}
                </Paper>
              </StyledDiv>
            </InfiniteScroll>
          )}
        </Box>
        <Box
          sx={{
            maxWidth: "650px",
            margin: "0 auto 1rem auto",
          }}
        >
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="title"
            label={t("myDesign.zones.uploadVideo.titleVideo")}
            name="title"
            autoComplete="title"
            value={title}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setTitle(e.target.value)}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="description"
            label={t("myDesign.zones.uploadVideo.descriptionVideo")}
            name="description"
            autoComplete="description"
            value={description}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Box>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSaveModel}
          disabled={!selectedModel || !title || !description || isUpload}
        >
          {isUpload ? (
            <CircularProgress size={28} />
          ) : (
            t("myDesign.zones.redirect.saveButton")
          )}
        </Button>
      </StyledDiv>
    );
  };

  const DesignDeleted = () => {
    React.useEffect(() => {
      setTimeout(() => {
        dispatch(resetDeletedDesign());
        history("/");
      }, 3500);
    }, []);

    return (
      <Paper elevation={12} style={{ padding: "1rem", textAlign: "center" }}>
        <Typography variant="body1">
          {t("homePage.myDesign.designDoesntExist")}
        </Typography>
        <Typography variant="body1">
          {t("homePage.myDesign.designDoesntExistRedirect")}
        </Typography>
      </Paper>
    );
  };

  return (
    <Backdrop
      // className={classes.backdrop}
      open={backdropStatus || isDesignNull ? true : false}
      onClick={!loading ? () => dispatch(closeBackdrop()) : undefined}
      style={{
        zIndex: theme?.zIndex.drawer + 1,
        color: "#fff",
      }}
    >
      {loading && <LoadingComponent />}
      {backdropStatus && backdropFrom === "zone" && <ZoneActionMenu />}
      {backdropStatus && backdropFrom === "modify" && <ModifyContent />}
      {backdropStatus && backdropFrom === "ADS-app" && (
        <AdsApp backdropStatus={backdropStatus} />
      )}
      {backdropStatus && backdropFrom === "exparience" && (
        <ExparienceZoneActionMenu />
      )}
      {isDesignNull && <DesignDeleted />}
    </Backdrop>
  );
}

function CircularProgressWithLabel(props) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="caption" component="div" color="textSecondary">
          {`${isNaN(Math.round(props.value)) ? 0 : Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}
